import { useState, useEffect, useRef } from 'react';
import { client } from '../../sdkAdapter';
import ThemeSettingsModal from './ThemeSettingsModal';
import Swal from 'sweetalert2';

export default function ThemeGrid() {
    const [themes, setThemes] = useState<any[]>([]);
    const [activeTheme, setActiveTheme] = useState<any>(null);
    const [loading, setLoading] = useState(false);
    const [selectedThemeForSettings, setSelectedThemeForSettings] = useState<any>(null);
    const [isUploading, setIsUploading] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const fetchThemes = async () => {
        setLoading(true); 
        try {
            const [themesRes, activeRes] = await Promise.all([
                client.getApiThemes() as any,
                client.getApiThemeActive() as any
            ]);

            const themesData = themesRes.data?.data?.data || themesRes.data?.data || themesRes.data || [];
            setThemes(Array.isArray(themesData) ? themesData : []);

            // Active theme payload structure handling
            setActiveTheme(activeRes.data?.data || activeRes.data || null);
        } catch (err) {
            console.error('Failed to fetch theme data:', err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchThemes();
    }, []);

    const handleActivate = async (themeId: number) => {
        try {
            await client.postApiThemeActivateId({
                id: String(themeId),
                getApiSettingsCurrencyIdRequest: {} as any
            });
            fetchThemes();
        } catch (err) {
            console.error('Failed to activate theme:', err);
            Swal.fire({
                title: 'Error',
                text: 'Failed to activate theme',
                icon: 'error'
            });
        }
    };

    const handleUploadClick = () => {
        fileInputRef.current?.click();
    };

    const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        if (!file.name.endsWith('.zip')) {
            Swal.fire({
                title: 'Invalid File',
                text: 'Please select a .zip file',
                icon: 'warning'
            });
            return;
        }

        setIsUploading(true);
        try {
            await client.postApiThemeUpload({
                themeZip: file,
                name: file.name.replace('.zip', '')
            });
            Swal.fire({
                title: 'Success',
                text: 'Theme uploaded successfully!',
                icon: 'success',
                timer: 2000,
                showConfirmButton: false
            });
            fetchThemes();
        } catch (err: any) {
            console.error('Upload failed:', err);
            const errorMsg = err.response?.data?.message || 'Failed to upload theme. Check if the theme name already exists or the file is invalid.';
            Swal.fire({
                title: 'Error',
                text: errorMsg,
                icon: 'error'
            });
        } finally {
            setIsUploading(false);
            if (fileInputRef.current) fileInputRef.current.value = '';
        }
    };

    return (
        <div className="space-y-6">
            <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileChange}
                accept=".zip"
                className="hidden"
            />

            <div className="flex justify-between items-center">
                <div>
                    <h3 className="text-xl font-semibold text-gray-800 dark:text-white">Installed Themes</h3>
                    <p className="text-sm text-gray-500 mt-1">Manage and customize your store themes.</p>
                </div>
                <div className="flex gap-3 items-center">
                    {loading && <span className="text-sm text-gray-500 italic animate-pulse">Syncing...</span>}
                    <button
                        onClick={fetchThemes}
                        className="p-2.5 text-gray-400 hover:text-brand-500 hover:bg-brand-50 rounded-xl transition-all"
                        title="Refresh list"
                    >
                        <svg className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                        </svg>
                    </button>
                    <button
                        onClick={handleUploadClick}
                        disabled={isUploading}
                        className="flex items-center gap-2 px-5 py-2.5 bg-brand-500 text-white font-semibold rounded-xl hover:bg-brand-600 transition-all shadow-lg shadow-brand-500/20 disabled:opacity-50"
                    >
                        {isUploading ? (
                            <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                            </svg>
                        ) : (
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                            </svg>
                        )}
                        Upload New Theme
                    </button>
                </div>
            </div>

            {themes.length === 0 ? (
                <div className="text-center p-16 bg-white dark:bg-gray-800 rounded-2xl border-2 border-dashed border-gray-200 dark:border-gray-700">
                    <div className="w-20 h-20 bg-gray-50 dark:bg-gray-700/50 rounded-full flex items-center justify-center mx-auto mb-6">
                        <svg className="w-10 h-10 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                    </div>
                    <h4 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">Build your design identity</h4>
                    <p className="text-gray-500 dark:text-gray-400 mb-8 max-w-sm mx-auto text-lg leading-relaxed">Choose a starting point or upload your custom theme to begin customizing your store's appearance.</p>
                    <button
                        onClick={handleUploadClick}
                        disabled={isUploading}
                        className="px-8 py-3 bg-gray-900 dark:bg-white dark:text-gray-900 text-white font-bold rounded-xl hover:opacity-90 transition-all flex items-center gap-2 mx-auto shadow-xl"
                    >
                        {isUploading ? 'Uploading...' : 'Get Started & Upload'}
                    </button>
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                    {themes.map(theme => (
                        <div key={theme.id} className={`group bg-white dark:bg-gray-800 rounded-2xl border-2 overflow-hidden transition-all hover:shadow-2xl hover:-translate-y-1 ${activeTheme?.id === theme.id ? 'border-brand-500 ring-4 ring-brand-500/10' : 'border-gray-100 dark:border-gray-700'}`}>
                            <div className="aspect-[4/3] bg-gray-100 dark:bg-gray-900 flex items-center justify-center relative overflow-hidden">
                                <span className="text-gray-400 font-bold text-lg opacity-40 group-hover:scale-125 transition-transform duration-500">PREVIEW</span>
                                {activeTheme?.id === theme.id && (
                                    <div className="absolute top-4 right-4 px-3 py-1.5 bg-brand-500 text-white text-[10px] font-black uppercase tracking-widest rounded-full shadow-lg z-10 flex items-center gap-1.5">
                                        <span className="w-1.5 h-1.5 bg-white rounded-full animate-pulse"></span>
                                        Active
                                    </div>
                                )}
                            </div>
                            <div className="p-6">
                                <h4 className="font-bold text-gray-900 dark:text-white text-lg mb-2">{theme.name}</h4>
                                <p className="text-sm text-gray-500 dark:text-gray-400 mb-8 line-clamp-2 h-10 leading-relaxed font-medium">{theme.description || 'Customizable e-commerce theme for Shoplattice. Includes modular layout and responsive design.'}</p>
                                <div className="flex justify-between items-center pt-4 border-t border-gray-100 dark:border-gray-700">
                                    <button
                                        onClick={() => setSelectedThemeForSettings(theme)}
                                        className="text-sm font-bold text-brand-600 hover:text-brand-700 dark:text-brand-400 dark:hover:text-brand-300 flex items-center gap-1 group/btn"
                                    >
                                        Settings
                                        <svg className="w-4 h-4 group-hover/btn:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                        </svg>
                                    </button>
                                    {activeTheme?.id !== theme.id && (
                                        <button
                                            onClick={() => handleActivate(theme.id)}
                                            className="px-5 py-2 text-xs font-black uppercase tracking-[0.2em] bg-gray-900 dark:bg-white dark:text-gray-900 text-white rounded-lg hover:opacity-90 transition-all active:scale-95"
                                        >
                                            Activate
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {selectedThemeForSettings && (
                <ThemeSettingsModal
                    theme={selectedThemeForSettings}
                    isOpen={!!selectedThemeForSettings}
                    onClose={() => setSelectedThemeForSettings(null)}
                    onSave={() => {
                        fetchThemes();
                        setSelectedThemeForSettings(null);
                    }}
                />
            )}
        </div>
    );
}
